/**
 * Iter 154 · Phase B1 — MSME Essentials console.
 *
 * Route: /app/finance/msme
 *
 * Three tabs (following the accounting-safe patterns implemented in the backend):
 *   • Recurring Invoices — template CRUD + "Generate now" (idempotent).
 *   • Credit Notes       — list, create, apply (posts reversal journal), void.
 *   • Convert Dashboard  — visibility of Quotation→SO / SO→Invoice conversion
 *                          state + "already converted" badges + force-convert
 *                          with confirm.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, RotateCw, FileMinus, Play, Pause, Trash2, ArrowRight, AlertTriangle, CheckCircle2, Zap, Repeat, Upload, Landmark, ShieldCheck, TrendingUp, Wallet, Sparkles, Bell, Palette, Download, BellRing } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

function fmt(n, ccy = "INR") {
  const v = Number(n || 0);
  const sym = ccy === "INR" ? "₹" : ccy === "USD" ? "$" : ccy === "EUR" ? "€" : `${ccy} `;
  if (Math.abs(v) >= 1e5) return `${sym}${(v / 1e5).toFixed(2)}L`;
  if (Math.abs(v) >= 1e3) return `${sym}${(v / 1e3).toFixed(1)}K`;
  return `${sym}${v.toFixed(0)}`;
}

export default function MsmeEssentials() {
  const { activeWorkspaceId } = useAuth();
  return (
    <>
      <PageHeader
        breadcrumb="Finance"
        title="MSME Compliance"
        description="Recurring invoices · Credit notes · Convert flows with duplicate protection"
        testid="msme-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-4" data-testid="msme">
        <Tabs defaultValue="recurring" className="w-full">
          <TabsList className="rounded-none flex-wrap h-auto">
            <TabsTrigger value="recurring" className="rounded-none" data-testid="msme-tab-recurring">Recurring invoices</TabsTrigger>
            <TabsTrigger value="credit-notes" className="rounded-none" data-testid="msme-tab-cn">Credit notes</TabsTrigger>
            <TabsTrigger value="debit-notes" className="rounded-none" data-testid="msme-tab-dn">Debit notes</TabsTrigger>
            <TabsTrigger value="convert" className="rounded-none" data-testid="msme-tab-convert">Convert dashboard</TabsTrigger>
            <TabsTrigger value="bank-recon" className="rounded-none" data-testid="msme-tab-bank">Bank reconciliation</TabsTrigger>
            <TabsTrigger value="three-way" className="rounded-none" data-testid="msme-tab-3wm">PO 3-way match</TabsTrigger>
            <TabsTrigger value="tax" className="rounded-none" data-testid="msme-tab-tax">Tax &amp; compliance</TabsTrigger>
            <TabsTrigger value="cfo" className="rounded-none" data-testid="msme-tab-cfo">CFO Copilot</TabsTrigger>
            <TabsTrigger value="templates" className="rounded-none" data-testid="msme-tab-templates">Doc templates</TabsTrigger>
            <TabsTrigger value="notif-prefs" className="rounded-none" data-testid="msme-tab-notif">Notifications</TabsTrigger>
            <TabsTrigger value="exports" className="rounded-none" data-testid="msme-tab-exports">Exports</TabsTrigger>
          </TabsList>
          <TabsContent value="recurring" className="pt-4">
            <RecurringPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="credit-notes" className="pt-4">
            <CreditNotesPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="debit-notes" className="pt-4">
            <DebitNotesPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="convert" className="pt-4">
            <ConvertPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="bank-recon" className="pt-4">
            <BankReconPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="three-way" className="pt-4">
            <ThreeWayMatchPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="tax" className="pt-4">
            <TaxCompliancePane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="cfo" className="pt-4">
            <CfoCopilotPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="templates" className="pt-4">
            <DocTemplatesPane wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="notif-prefs" className="pt-4">
            <NotificationPrefsPane />
          </TabsContent>
          <TabsContent value="exports" className="pt-4">
            <LedgerExportPane wsId={activeWorkspaceId} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// ─────────── Recurring pane ───────────

function RecurringPane({ wsId }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    if (!wsId) return;
    try { const r = await api.get(`/workspaces/${wsId}/recurring-invoices`); setRows(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => {
    try { await api.patch(`/recurring-invoices/${r.id}`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const generate = async (r) => {
    try {
      const g = await api.post(`/recurring-invoices/${r.id}/generate-now`);
      toast.success(`Invoice generated · ${g.data.invoice_id?.slice(0, 8)}…`); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Generate failed")); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete template "${r.template_name}"? Already-generated invoices remain.`)) return;
    try { await api.delete(`/recurring-invoices/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-3" data-testid="msme-recurring">
      <div className="flex items-center gap-2">
        <div className="text-xs text-muted-foreground italic">
          Templates never touch the ledger — child invoices are created on schedule and post to the ledger on <b>sent</b>.
        </div>
        <Button size="sm" className="rounded-none ml-auto" onClick={() => setCreating(true)} data-testid="msme-recurring-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New template
        </Button>
      </div>
      {rows === null ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-border p-6 text-center text-sm italic text-muted-foreground">
          No recurring templates yet. Create one for retainer clients & subscription revenue.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs" data-testid="msme-recurring-table">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
              <tr>
                <th className="text-left px-2 py-1.5">Template</th>
                <th className="text-left px-2 py-1.5">Client</th>
                <th className="text-left px-2 py-1.5">Freq</th>
                <th className="text-right px-2 py-1.5">Amount</th>
                <th className="text-left px-2 py-1.5">Next due</th>
                <th className="text-center px-2 py-1.5">Status</th>
                <th className="text-right px-2 py-1.5">Generated</th>
                <th className="text-right px-2 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`msme-recurring-row-${r.id}`}>
                  <td className="px-2 py-1.5 font-medium">{r.template_name}</td>
                  <td className="px-2 py-1.5 truncate max-w-[180px]">{r.client_name}</td>
                  <td className="px-2 py-1.5"><span className="chip text-[10px]"><Repeat className="w-2.5 h-2.5 inline mr-0.5" />{r.frequency}{r.interval > 1 ? ` ×${r.interval}` : ""}</span></td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(r.grand_total, r.currency)}</td>
                  <td className="px-2 py-1.5 font-mono">{r.next_due_at || "—"}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`chip text-[10px] ${r.status === "active" ? "chip-approved" : r.status === "paused" ? "chip-warning" : ""}`}>{r.status}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{(r.generated_invoice_ids || []).length}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="rounded-none h-6 px-1" onClick={() => generate(r)}
                            disabled={r.status !== "active"} title="Generate next invoice now"
                            data-testid={`msme-recurring-gen-${r.id}`}>
                      <Zap className="w-3 h-3" />
                    </Button>
                    {r.status === "active"
                      ? <Button size="sm" variant="ghost" className="rounded-none h-6 px-1" onClick={() => setStatus(r, "paused")} title="Pause"><Pause className="w-3 h-3" /></Button>
                      : <Button size="sm" variant="ghost" className="rounded-none h-6 px-1" onClick={() => setStatus(r, "active")} title="Resume"><Play className="w-3 h-3" /></Button>}
                    <Button size="sm" variant="ghost" className="rounded-none h-6 px-1 text-destructive" onClick={() => del(r)} title="Delete"><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewRecurringDialog wsId={wsId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewRecurringDialog({ wsId, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    template_name: "", client_id: "", currency: "INR", grand_total: "",
    frequency: "monthly", interval: 1, start_date: new Date().toISOString().slice(0, 10),
    day_of_month: "", auto_send: false, notes: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/clients`).then((r) => setClients(r.data || []));
  }, [wsId]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!form.template_name || !form.client_id || !form.grand_total) return toast.error("Name, client & amount required");
    setBusy(true);
    try {
      const gt = Number(form.grand_total);
      const tax = +(gt * 18 / 118).toFixed(2);  // assume 18% GST-inclusive by default
      await api.post(`/workspaces/${wsId}/recurring-invoices`, {
        template_name: form.template_name, client_id: form.client_id, currency: form.currency,
        items: [{ description: form.template_name, qty: 1, rate: gt - tax, tax_pct: 18, line_total: gt }],
        subtotal: gt - tax, tax_total: tax, grand_total: gt,
        frequency: form.frequency, interval: Number(form.interval),
        start_date: form.start_date,
        day_of_month: form.day_of_month ? Number(form.day_of_month) : null,
        auto_send: form.auto_send, notes: form.notes,
      });
      toast.success("Template created");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="msme-recurring-new-dialog">
        <DialogHeader><DialogTitle>New recurring invoice template</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Template name</Label>
            <Input value={form.template_name} onChange={(e) => set("template_name", e.target.value)} className="h-9 rounded-none" data-testid="msme-recur-name" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Client</Label>
            <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
              <SelectTrigger className="h-9 rounded-none" data-testid="msme-recur-client"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.slice(0, 100).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Grand total (incl. GST)</Label>
            <Input type="number" value={form.grand_total} onChange={(e) => set("grand_total", e.target.value)} className="h-9 rounded-none" data-testid="msme-recur-amount" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>{["INR", "USD", "EUR", "GBP", "AED", "SGD"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Frequency</Label>
            <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger className="h-9 rounded-none" data-testid="msme-recur-freq"><SelectValue /></SelectTrigger>
              <SelectContent>{["daily", "weekly", "monthly", "quarterly", "yearly"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Interval (every N)</Label>
            <Input type="number" value={form.interval} onChange={(e) => set("interval", e.target.value)} className="h-9 rounded-none" min="1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Start date</Label>
            <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="h-9 rounded-none" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Day of month (optional)</Label>
            <Input type="number" min="1" max="31" value={form.day_of_month} onChange={(e) => set("day_of_month", e.target.value)} className="h-9 rounded-none" placeholder="1-31" />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" checked={form.auto_send} onChange={(e) => set("auto_send", e.target.checked)} data-testid="msme-recur-autosend" />
            <Label className="text-xs">Auto-mark generated invoices as <b>sent</b> (posts to ledger immediately)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="msme-recur-save">
            {busy ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Credit notes pane ───────────

function CreditNotesPane({ wsId }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    if (!wsId) return;
    try { const r = await api.get(`/workspaces/${wsId}/credit-notes`); setRows(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const apply = async (cn) => {
    if (!window.confirm(`Apply credit note ${cn.doc_no}? This posts a reversal journal + reduces the invoice balance.`)) return;
    try { await api.post(`/credit-notes/${cn.id}/apply`); toast.success("Applied · reversal journal posted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
  };
  const doVoid = async (cn) => {
    if (!window.confirm(`Void ${cn.doc_no}? ${cn.status === "applied" ? "The applied reversal will be reversed." : ""}`)) return;
    try { await api.post(`/credit-notes/${cn.id}/void`); toast.success("Voided"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Void failed")); }
  };

  return (
    <div className="space-y-3" data-testid="msme-cn">
      <div className="flex items-center gap-2">
        <div className="text-xs text-muted-foreground italic">
          Credit notes reduce invoice liability. Applying posts a <b>reverse journal</b> (Cr AR / Dr Revenue &amp; Output-GST) so both books stay balanced.
        </div>
        <Button size="sm" className="rounded-none ml-auto" onClick={() => setCreating(true)} data-testid="msme-cn-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New credit note
        </Button>
      </div>
      {rows === null ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <div className="border border-border p-6 text-center text-sm italic text-muted-foreground">No credit notes yet.</div>
        : (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-xs" data-testid="msme-cn-table">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
                <tr>
                  <th className="text-left px-2 py-1.5">CN No</th>
                  <th className="text-left px-2 py-1.5">Invoice</th>
                  <th className="text-left px-2 py-1.5">Client</th>
                  <th className="text-left px-2 py-1.5">Reason</th>
                  <th className="text-right px-2 py-1.5">Total</th>
                  <th className="text-center px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">Journal</th>
                  <th className="text-right px-2 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((cn) => (
                  <tr key={cn.id} className="border-t border-border" data-testid={`msme-cn-row-${cn.id}`}>
                    <td className="px-2 py-1.5 font-mono">{cn.doc_no}</td>
                    <td className="px-2 py-1.5 font-mono">{cn.invoice_no}</td>
                    <td className="px-2 py-1.5 truncate max-w-[160px]">{cn.client_name}</td>
                    <td className="px-2 py-1.5"><span className="chip text-[10px]">{cn.reason}</span></td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(cn.total, cn.currency)}</td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`chip text-[10px] ${cn.status === "applied" ? "chip-approved" : cn.status === "void" ? "chip-rejected" : ""}`}>{cn.status}</span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">{cn.journal_id ? cn.journal_id.slice(0, 8) + "…" : "—"}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {cn.status === "draft" && (
                        <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]" onClick={() => apply(cn)} data-testid={`msme-cn-apply-${cn.id}`}>
                          Apply
                        </Button>
                      )}
                      {cn.status !== "void" && (
                        <Button size="sm" variant="ghost" className="rounded-none h-6 px-1 text-destructive" onClick={() => doVoid(cn)} title="Void" data-testid={`msme-cn-void-${cn.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {creating && <NewCreditNoteDialog wsId={wsId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewCreditNoteDialog({ wsId, onClose, onSaved }) {
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({ invoice_id: "", reason: "return", total: "", tax_total: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/invoices?status=sent`).then((r) => setInvoices(r.data || []));
  }, [wsId]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const invSelected = useMemo(() => invoices.find((i) => i.id === form.invoice_id), [invoices, form.invoice_id]);
  const save = async () => {
    if (!form.invoice_id || !form.total) return toast.error("Invoice & total required");
    const total = Number(form.total);
    const tax_total = form.tax_total ? Number(form.tax_total) : +(total * 18 / 118).toFixed(2);
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/credit-notes`, {
        invoice_id: form.invoice_id, reason: form.reason,
        total, tax_total,
        items: [{ description: form.reason, qty: 1, rate: total - tax_total, tax_pct: 18 }],
        notes: form.notes,
      });
      toast.success("Credit note drafted");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="msme-cn-new-dialog">
        <DialogHeader><DialogTitle>New credit note</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Against invoice</Label>
            <Select value={form.invoice_id} onValueChange={(v) => set("invoice_id", v)}>
              <SelectTrigger className="h-9 rounded-none" data-testid="msme-cn-invoice"><SelectValue placeholder="Pick invoice" /></SelectTrigger>
              <SelectContent>
                {invoices.slice(0, 100).map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.doc_no} · {i.client_name} · {fmt(i.grand_total, i.currency)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {invSelected && (
            <div className="text-[10px] text-muted-foreground bg-muted/30 p-2">
              Remaining creditable: <b>{fmt(invSelected.balance_due, invSelected.currency)}</b>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Reason</Label>
              <Select value={form.reason} onValueChange={(v) => set("reason", v)}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{["return", "discount", "price_adjust", "tax_correction", "other"].map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Total (incl. GST)</Label>
              <Input type="number" value={form.total} onChange={(e) => set("total", e.target.value)} className="h-9 rounded-none" data-testid="msme-cn-total" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Tax portion (auto: 18% GST)</Label>
              <Input type="number" value={form.tax_total} onChange={(e) => set("tax_total", e.target.value)} className="h-9 rounded-none" placeholder="Leave blank for 18%" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="msme-cn-save">
            {busy ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Convert dashboard ───────────

function ConvertPane({ wsId }) {
  const nav = useNavigate();
  const [quotations, setQuotations] = useState(null);
  const [salesOrders, setSalesOrders] = useState(null);
  const [confirmForce, setConfirmForce] = useState(null);

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const [q, s] = await Promise.all([
        api.get(`/workspaces/${wsId}/quotations`),
        api.get(`/workspaces/${wsId}/sales-orders`),
      ]);
      setQuotations(q.data); setSalesOrders(s.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const convertQtoI = async (q, force = false) => {
    try {
      const r = await api.post(`/quotations/${q.id}/convert-to-invoice${force ? "?force=true" : ""}`);
      toast.success(`Invoice ${r.data.doc_no} created`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === "already_converted") {
        setConfirmForce({ kind: "q2i", item: q, existing_id: detail.existing_invoice_id });
      } else toast.error(apiErrorMessage(e, "Convert failed"));
    }
  };
  const convertQtoSO = async (q, force = false) => {
    try {
      const r = await api.post(`/quotations/${q.id}/convert-to-so${force ? "?force=true" : ""}`);
      toast.success(`SO ${r.data.doc_no} created`); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === "already_converted") {
        setConfirmForce({ kind: "q2so", item: q, existing_id: detail.existing_so_id });
      } else toast.error(apiErrorMessage(e, "Convert failed"));
    }
  };
  const convertSOtoI = async (so, force = false) => {
    try {
      const r = await api.post(`/sales-orders/${so.id}/convert-to-invoice${force ? "?force=true" : ""}`);
      toast.success(`Invoice ${r.data.doc_no} created`); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === "already_converted") {
        setConfirmForce({ kind: "so2i", item: so, existing_id: detail.existing_invoice_id });
      } else toast.error(apiErrorMessage(e, "Convert failed"));
    }
  };

  const doForce = async () => {
    const c = confirmForce; setConfirmForce(null);
    if (c.kind === "q2i") return convertQtoI(c.item, true);
    if (c.kind === "q2so") return convertQtoSO(c.item, true);
    if (c.kind === "so2i") return convertSOtoI(c.item, true);
  };

  if (quotations === null || salesOrders === null) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="msme-convert">
      <div className="text-xs text-muted-foreground italic">
        Convert flows are protected against duplicates. If a downstream doc already exists, the button turns green with a check ✓ — click again for a confirm-and-force dialog.
      </div>

      <section className="border border-border" data-testid="msme-convert-quotations">
        <div className="px-3 py-2 border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-widest">
          Quotations · {quotations.length}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20">
              <tr>
                <th className="text-left px-2 py-1.5">Quotation</th>
                <th className="text-left px-2 py-1.5">Client</th>
                <th className="text-right px-2 py-1.5">Total</th>
                <th className="text-center px-2 py-1.5">Status</th>
                <th className="text-center px-2 py-1.5">→ SO</th>
                <th className="text-center px-2 py-1.5">→ Invoice</th>
              </tr>
            </thead>
            <tbody>
              {quotations.slice(0, 50).map((q) => (
                <tr key={q.id} className="border-t border-border" data-testid={`msme-convert-q-${q.id}`}>
                  <td className="px-2 py-1.5 font-mono">{q.doc_no}</td>
                  <td className="px-2 py-1.5 truncate max-w-[160px]">{q.client_name}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(q.grand_total, q.currency)}</td>
                  <td className="px-2 py-1.5 text-center"><span className="chip text-[10px]">{q.status}</span></td>
                  <td className="px-2 py-1.5 text-center">
                    <ConvertButton
                      isDone={!!q.converted_so_id}
                      onFresh={() => convertQtoSO(q, false)}
                      onView={() => nav(`/app/sales/orders/${q.converted_so_id}`)}
                      testid={`msme-btn-q2so-${q.id}`}
                      label="Create SO"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <ConvertButton
                      isDone={!!q.converted_invoice_id}
                      onFresh={() => convertQtoI(q, false)}
                      onView={() => nav(`/app/sales/invoices/${q.converted_invoice_id}`)}
                      testid={`msme-btn-q2i-${q.id}`}
                      label="Create invoice"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border border-border" data-testid="msme-convert-sos">
        <div className="px-3 py-2 border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-widest">
          Sales Orders · {salesOrders.length}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20">
              <tr>
                <th className="text-left px-2 py-1.5">SO No</th>
                <th className="text-left px-2 py-1.5">Client</th>
                <th className="text-right px-2 py-1.5">Total</th>
                <th className="text-center px-2 py-1.5">Status</th>
                <th className="text-center px-2 py-1.5">→ Invoice</th>
              </tr>
            </thead>
            <tbody>
              {salesOrders.slice(0, 50).map((so) => (
                <tr key={so.id} className="border-t border-border" data-testid={`msme-convert-so-${so.id}`}>
                  <td className="px-2 py-1.5 font-mono">{so.doc_no}</td>
                  <td className="px-2 py-1.5 truncate max-w-[160px]">{so.client_name}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(so.grand_total, so.currency)}</td>
                  <td className="px-2 py-1.5 text-center"><span className="chip text-[10px]">{so.status}</span></td>
                  <td className="px-2 py-1.5 text-center">
                    <ConvertButton
                      isDone={!!so.converted_invoice_id}
                      onFresh={() => convertSOtoI(so, false)}
                      onView={() => nav(`/app/sales/invoices/${so.converted_invoice_id}`)}
                      testid={`msme-btn-so2i-${so.id}`}
                      label="Create invoice"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <AlertDialog open={!!confirmForce} onOpenChange={(o) => { if (!o) setConfirmForce(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="msme-convert-force-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Already converted</AlertDialogTitle>
            <AlertDialogDescription>
              A downstream document already exists (id <code className="text-xs">{confirmForce?.existing_id?.slice(0, 8)}…</code>). Force-convert creates a <b>new duplicate</b>. Only do this if you understand the accounting impact (e.g. the original was cancelled).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={doForce} data-testid="msme-convert-force-confirm">Force create duplicate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConvertButton({ isDone, onFresh, onView, testid, label }) {
  if (isDone) {
    return (
      <button type="button" onClick={onView}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
              data-testid={testid} title="Already converted — click to open">
        <CheckCircle2 className="w-3 h-3" /> Converted
      </button>
    );
  }
  return (
    <button type="button" onClick={onFresh}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border border-primary text-primary hover:bg-primary/10"
            data-testid={testid} title={label}>
      <ArrowRight className="w-3 h-3" /> {label}
    </button>
  );
}

// ─────────── Debit Notes pane — mirrors CreditNotesPane, bill-side ───────────

function DebitNotesPane({ wsId }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    if (!wsId) return;
    try { const r = await api.get(`/workspaces/${wsId}/debit-notes`); setRows(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const apply = async (dn) => {
    if (!window.confirm(`Apply debit note ${dn.doc_no}? Posts reverse journal + reduces bill balance.`)) return;
    try { await api.post(`/debit-notes/${dn.id}/apply`); toast.success("Applied · reversal journal posted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
  };
  const doVoid = async (dn) => {
    if (!window.confirm(`Void ${dn.doc_no}?`)) return;
    try { await api.post(`/debit-notes/${dn.id}/void`); toast.success("Voided"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Void failed")); }
  };

  return (
    <div className="space-y-3" data-testid="msme-dn">
      <div className="flex items-center gap-2">
        <div className="text-xs text-muted-foreground italic">
          Debit notes reduce vendor bill liability. Applying posts a <b>reverse journal</b> (Dr AP / Cr Purchases &amp; Input-GST).
        </div>
        <Button size="sm" className="rounded-none ml-auto" onClick={() => setCreating(true)} data-testid="msme-dn-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New debit note
        </Button>
      </div>
      {rows === null ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <div className="border border-border p-6 text-center text-sm italic text-muted-foreground">No debit notes yet.</div>
        : (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-xs" data-testid="msme-dn-table">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
                <tr>
                  <th className="text-left px-2 py-1.5">DN No</th>
                  <th className="text-left px-2 py-1.5">Bill</th>
                  <th className="text-left px-2 py-1.5">Vendor</th>
                  <th className="text-left px-2 py-1.5">Reason</th>
                  <th className="text-right px-2 py-1.5">Total</th>
                  <th className="text-center px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">Journal</th>
                  <th className="text-right px-2 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((dn) => (
                  <tr key={dn.id} className="border-t border-border" data-testid={`msme-dn-row-${dn.id}`}>
                    <td className="px-2 py-1.5 font-mono">{dn.doc_no}</td>
                    <td className="px-2 py-1.5 font-mono">{dn.bill_no || "—"}</td>
                    <td className="px-2 py-1.5 truncate max-w-[160px]">{dn.vendor_name}</td>
                    <td className="px-2 py-1.5"><span className="chip text-[10px]">{dn.reason}</span></td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(dn.total, dn.currency)}</td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`chip text-[10px] ${dn.status === "applied" ? "chip-approved" : dn.status === "void" ? "chip-rejected" : ""}`}>{dn.status}</span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">{dn.journal_id ? dn.journal_id.slice(0, 8) + "…" : "—"}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {dn.status === "draft" && (
                        <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]" onClick={() => apply(dn)} data-testid={`msme-dn-apply-${dn.id}`}>
                          Apply
                        </Button>
                      )}
                      {dn.status !== "void" && (
                        <Button size="sm" variant="ghost" className="rounded-none h-6 px-1 text-destructive" onClick={() => doVoid(dn)} title="Void" data-testid={`msme-dn-void-${dn.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {creating && <NewDebitNoteDialog wsId={wsId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewDebitNoteDialog({ wsId, onClose, onSaved }) {
  const [bills, setBills] = useState([]);
  const [form, setForm] = useState({ bill_id: "", reason: "return", total: "", tax_total: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/bills`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.bills || []);
      setBills(list);
    }).catch(() => setBills([]));
  }, [wsId]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!form.bill_id || !form.total) return toast.error("Bill & total required");
    const total = Number(form.total);
    const tax_total = form.tax_total ? Number(form.tax_total) : +(total * 18 / 118).toFixed(2);
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/debit-notes`, {
        bill_id: form.bill_id, reason: form.reason,
        total, tax_total,
        items: [{ description: form.reason, qty: 1, rate: total - tax_total, tax_pct: 18 }],
        notes: form.notes,
      });
      toast.success("Debit note drafted");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="msme-dn-new-dialog">
        <DialogHeader><DialogTitle>New debit note</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Against bill</Label>
            <Select value={form.bill_id} onValueChange={(v) => set("bill_id", v)}>
              <SelectTrigger className="h-9 rounded-none" data-testid="msme-dn-bill"><SelectValue placeholder="Pick bill" /></SelectTrigger>
              <SelectContent>
                {bills.slice(0, 100).map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.bill_no || b.doc_no} · {b.vendor_name} · {fmt(b.grand_total, b.currency)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Reason</Label>
              <Select value={form.reason} onValueChange={(v) => set("reason", v)}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{["return", "discount", "price_adjust", "tax_correction", "other"].map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Total (incl. GST)</Label>
              <Input type="number" value={form.total} onChange={(e) => set("total", e.target.value)} className="h-9 rounded-none" data-testid="msme-dn-total" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="msme-dn-save">
            {busy ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Bank Reconciliation pane ───────────

function BankReconPane({ wsId }) {
  const [accts, setAccts] = useState([]);
  const [statements, setStatements] = useState(null);
  const [selectedStmt, setSelectedStmt] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const [a, s] = await Promise.all([
        api.get(`/workspaces/${wsId}/bank-accounts`),
        api.get(`/workspaces/${wsId}/bank-statements`),
      ]);
      setAccts(a.data); setStatements(s.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const autoMatch = async (s) => {
    try {
      const r = await api.post(`/bank-statements/${s.id}/auto-match?tolerance_days=3`);
      toast.success(`${r.data.matched} txns auto-matched`);
      load();
      if (selectedStmt?.id === s.id) openDetail(s);
    } catch (e) { toast.error(apiErrorMessage(e, "Auto-match failed")); }
  };
  const openDetail = async (s) => {
    try {
      const r = await api.get(`/bank-statements/${s.id}`);
      setSelectedStmt(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Open failed")); }
  };

  return (
    <div className="space-y-3" data-testid="msme-bank">
      <div className="flex items-center gap-2">
        <div className="text-xs text-muted-foreground italic">
          Read-only recon: auto-match by amount + date ±3d. Never posts to ledger without your say-so.
        </div>
        <Button size="sm" className="rounded-none ml-auto" onClick={() => setUploadOpen(true)} data-testid="msme-bank-upload"
                disabled={accts.length === 0}>
          <Upload className="w-3.5 h-3.5 mr-1" /> Upload statement
        </Button>
      </div>
      {accts.length === 0 && (
        <div className="border border-border p-4 text-xs italic text-muted-foreground">
          No bank accounts yet. Create one via API or ask your admin.
        </div>
      )}
      {statements === null ? <div className="text-sm text-muted-foreground">Loading…</div>
        : statements.length === 0 ? <div className="border border-border p-6 text-center text-sm italic text-muted-foreground">No statements uploaded yet.</div>
        : (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-xs" data-testid="msme-bank-stmts">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
                <tr>
                  <th className="text-left px-2 py-1.5">Period</th>
                  <th className="text-right px-2 py-1.5">Rows</th>
                  <th className="text-right px-2 py-1.5">Matched</th>
                  <th className="text-right px-2 py-1.5">Unreconciled</th>
                  <th className="text-right px-2 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((s) => (
                  <tr key={s.id} className="border-t border-border" data-testid={`msme-bank-stmt-${s.id}`}>
                    <td className="px-2 py-1.5 font-mono">{s.period_start} → {s.period_end}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{s.row_count}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-600">{s.reconciled_count}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-amber-600">{s.unreconciled_count}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]" onClick={() => autoMatch(s)}
                              data-testid={`msme-bank-auto-${s.id}`}>
                        <Landmark className="w-3 h-3 mr-1" /> Auto-match
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-6 px-1.5 text-[10px]" onClick={() => openDetail(s)}
                              data-testid={`msme-bank-open-${s.id}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {selectedStmt && (
        <div className="border border-border" data-testid="msme-bank-detail">
          <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold flex items-center gap-2">
            Statement {selectedStmt.statement.period_start} → {selectedStmt.statement.period_end} · {selectedStmt.txns.length} txns
            <Button size="sm" variant="ghost" className="rounded-none h-6 ml-auto" onClick={() => setSelectedStmt(null)}>Close</Button>
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20">
              <tr>
                <th className="text-left px-2 py-1">Date</th>
                <th className="text-left px-2 py-1">Description</th>
                <th className="text-right px-2 py-1">Amount</th>
                <th className="text-center px-2 py-1">Status</th>
                <th className="text-left px-2 py-1">Matched</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {selectedStmt.txns.map((t) => (
                <tr key={t.id} className="border-t border-border" data-testid={`msme-bank-txn-${t.id}`}>
                  <td className="px-2 py-1">{t.date}</td>
                  <td className="px-2 py-1 truncate max-w-[240px] font-sans">{t.description}</td>
                  <td className={`px-2 py-1 text-right ${t.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(t.amount)}</td>
                  <td className="px-2 py-1 text-center">
                    <span className={`chip text-[10px] ${t.status === "matched" ? "chip-approved" : ""}`}>{t.status}</span>
                  </td>
                  <td className="px-2 py-1 text-[10px] text-muted-foreground">
                    {t.reconciled_with_id ? `${t.reconciled_with_kind}:${t.reconciled_with_id.slice(0, 8)}…`
                      : (
                        <Button size="sm" variant="outline" className="rounded-none h-5 px-1 text-[9px]"
                                data-testid={`msme-bank-suggest-${t.id}`}
                                onClick={async () => {
                                  try {
                                    const r = await api.post(`/bank-txns/${t.id}/suggest-payment`, { use_ai: true });
                                    if (!r.data.suggestion) return toast.info(r.data.note || "No suggestion");
                                    const s = r.data.suggestion;
                                    if (window.confirm(`AI suggests: pay ${fmt(s.amount, s.currency)} against invoice ${s.invoice_no} (${s.client_name}) — confidence ${(s.confidence * 100).toFixed(0)}%.\n\nReason: ${s.reasoning}\n\nProceed?`)) {
                                      await api.post("/payments", { invoice_id: s.invoice_id, amount: s.amount, paid_at: s.paid_at });
                                      await api.post(`/bank-txns/${t.id}/match`, { kind: "payment", target_id: s.invoice_id });
                                      toast.success("Payment posted + matched");
                                    }
                                  } catch (e) { toast.error(apiErrorMessage(e, "Suggest failed")); }
                                }}>
                          <Zap className="w-2.5 h-2.5 mr-0.5" /> Suggest
                        </Button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {uploadOpen && (
        <UploadStatementDialog wsId={wsId} accts={accts} onClose={() => setUploadOpen(false)} onSaved={() => { setUploadOpen(false); load(); }} />
      )}
    </div>
  );
}

function UploadStatementDialog({ wsId, accts, onClose, onSaved }) {
  const [form, setForm] = useState({ bank_account_id: accts[0]?.id || "", period_start: "", period_end: "", csv: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!form.bank_account_id || !form.csv.trim()) return toast.error("Bank account + CSV rows required");
    // Parse CSV: date,description,amount[,reference]
    const rows = form.csv.trim().split("\n").map((line, ix) => {
      const [d, desc, amt, ref] = line.split(",").map((s) => s.trim());
      if (!d || amt === undefined) throw new Error(`Bad row #${ix + 1}`);
      return { date: d, description: desc || "", amount: Number(amt), reference: ref || null };
    });
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/bank-statements/upload`, {
        bank_account_id: form.bank_account_id,
        period_start: form.period_start || rows[0].date,
        period_end: form.period_end || rows[rows.length - 1].date,
        rows,
      });
      toast.success(`Uploaded ${r.data.inserted} · skipped ${r.data.skipped_duplicates}`);
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="msme-bank-upload-dialog">
        <DialogHeader><DialogTitle>Upload bank statement</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Bank account</Label>
            <Select value={form.bank_account_id} onValueChange={(v) => set("bank_account_id", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue placeholder="Pick account" /></SelectTrigger>
              <SelectContent>{accts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Period start</Label>
              <Input type="date" value={form.period_start} onChange={(e) => set("period_start", e.target.value)} className="h-9 rounded-none" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Period end</Label>
              <Input type="date" value={form.period_end} onChange={(e) => set("period_end", e.target.value)} className="h-9 rounded-none" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">CSV rows — one per line: date,description,amount[,reference]</Label>
            <Textarea rows={8} value={form.csv} onChange={(e) => set("csv", e.target.value)} className="rounded-none font-mono text-xs" data-testid="msme-bank-csv"
                      placeholder="2026-07-05,NEFT-Client-INV0001,11800,REF001&#10;2026-07-08,IMPS-Vendor,-2500,REF002" />
          </div>
          <div className="text-[10px] text-muted-foreground italic">
            Positive amount = money in (credits/customer payments). Negative = money out (vendor payments).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="msme-bank-upload-save">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── PO 3-Way Match pane ───────────

function ThreeWayMatchPane({ wsId }) {
  const [pos, setPos] = useState(null);
  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const r = await api.get(`/workspaces/${wsId}/purchase-orders`);
      setPos(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="msme-3wm">
      <div className="text-xs text-muted-foreground italic">
        Standard ERP 3-way match: PO qty ≥ GRN received ≥ Bill qty · Bill rate ≤ PO rate. Mismatches surface as warnings — never auto-block.
      </div>
      {pos === null ? <div className="text-sm text-muted-foreground">Loading…</div>
        : pos.length === 0 ? <div className="border border-border p-6 text-center text-sm italic text-muted-foreground">No purchase orders yet. Create one via API.</div>
        : (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-xs" data-testid="msme-3wm-table">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
                <tr>
                  <th className="text-left px-2 py-1.5">PO</th>
                  <th className="text-left px-2 py-1.5">Vendor</th>
                  <th className="text-right px-2 py-1.5">Total</th>
                  <th className="text-center px-2 py-1.5">Status</th>
                  <th className="text-right px-2 py-1.5">Lines received</th>
                  <th className="text-right px-2 py-1.5">GRNs</th>
                  <th className="text-right px-2 py-1.5">Bills linked</th>
                </tr>
              </thead>
              <tbody>
                {pos.map((po) => {
                  const total_ordered = po.items.reduce((a, i) => a + Number(i.qty || 0), 0);
                  const total_received = (po.received_qty_by_line || []).reduce((a, n) => a + Number(n || 0), 0);
                  return (
                    <tr key={po.id} className="border-t border-border" data-testid={`msme-3wm-po-${po.id}`}>
                      <td className="px-2 py-1.5 font-mono">{po.doc_no}</td>
                      <td className="px-2 py-1.5 truncate max-w-[160px]">{po.vendor_name}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmt(po.grand_total, po.currency)}</td>
                      <td className="px-2 py-1.5 text-center">
                        <span className={`chip text-[10px] ${po.status === "received" ? "chip-approved" : po.status === "partial_received" ? "chip-warning" : ""}`}>{po.status}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">{total_received}/{total_ordered}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{(po.receipt_ids || []).length}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{(po.bill_ids || []).length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      <div className="text-[10px] text-muted-foreground italic">
        Bills are linked + validated via API: <code>POST /bills/{'{bid}'}/three-way-match</code> with the PO id.
        Warnings (qty_over_grn, rate_over_po, vendor mismatch) surface in the response — decide whether to accept or reject.
      </div>
    </div>
  );
}

// ─────────── Tax & Compliance pane (TDS · IRN · E-way · GSTR-2A) ───────────

function TaxCompliancePane({ wsId }) {
  const [sections, setSections] = useState([]);
  const [invoice, setInvoice] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [preview, setPreview] = useState(null);
  const [irn, setIrn] = useState(null);
  const [eway, setEway] = useState(null);
  const [gstrPeriod, setGstrPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [gstrRecon, setGstrRecon] = useState(null);
  const [selectedSection, setSelectedSection] = useState("194J");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/tds-sections`).then((r) => setSections(r.data || []));
    api.get(`/workspaces/${wsId}/invoices?status=sent`).then((r) => setInvoices(r.data || []));
  }, [wsId]);

  const runTdsPreview = async () => {
    if (!invoice) return toast.error("Pick an invoice");
    setBusy(true);
    try {
      const r = await api.post(`/invoices/${invoice}/tds-preview`, { section_code: selectedSection });
      setPreview(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setBusy(false); }
  };
  const applyTds = async () => {
    if (!invoice || !preview) return;
    if (!window.confirm(`Apply TDS ₹${preview.tds_amount} to invoice? Balance reduces to ₹${(preview.grand_total - preview.tds_amount).toFixed(2)}.`)) return;
    try {
      await api.post(`/invoices/${invoice}/mark-tds-applied`, { section_code: selectedSection });
      toast.success("TDS applied · balance reduced");
      setPreview(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
  };
  const genIrn = async () => {
    if (!invoice) return toast.error("Pick invoice");
    try {
      const r = await api.post(`/invoices/${invoice}/generate-irn`, { dry_run: true });
      setIrn(r.data);
      toast.success("IRN generated (mock — live NIC needs prod creds)");
    } catch (e) { toast.error(apiErrorMessage(e, "IRN failed")); }
  };
  const genEway = async () => {
    if (!invoice) return toast.error("Pick invoice");
    try {
      const r = await api.post(`/invoices/${invoice}/generate-eway-bill`,
                               { transport_mode: "road", vehicle_no: "TBD", distance_km: 0, dry_run: true });
      setEway(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "E-way failed")); }
  };
  const runGstrRecon = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/gstr2a/reconcile?period=${gstrPeriod}`);
      setGstrRecon(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Recon failed")); }
  };

  return (
    <div className="space-y-4" data-testid="msme-tax">
      <div className="text-xs text-muted-foreground italic">
        <ShieldCheck className="w-3 h-3 inline mr-1" /> Indian tax &amp; compliance stack. TDS/TCS follow Section-code rate on <b>taxable value</b>. IRN + E-way run in mock mode until NIC production creds are set in workspace settings.
      </div>

      <section className="border border-border" data-testid="msme-tax-tds">
        <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold uppercase tracking-widest">
          TDS on Invoice
        </div>
        <div className="p-3 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Invoice</Label>
              <Select value={invoice} onValueChange={setInvoice}>
                <SelectTrigger className="h-9 rounded-none" data-testid="msme-tax-invoice"><SelectValue placeholder="Pick invoice" /></SelectTrigger>
                <SelectContent>
                  {invoices.slice(0, 100).map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.doc_no} · {i.client_name} · {fmt(i.grand_total, i.currency)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Section</Label>
              <Select value={selectedSection} onValueChange={setSelectedSection}>
                <SelectTrigger className="h-9 rounded-none" data-testid="msme-tax-section"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sections.map((s) => <SelectItem key={s.id} value={s.code}>{s.code} · {s.name} @ {s.rate}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-1">
              <Button size="sm" variant="outline" className="rounded-none" onClick={runTdsPreview} disabled={busy} data-testid="msme-tax-preview">Preview</Button>
              {preview && <Button size="sm" className="rounded-none" onClick={applyTds} data-testid="msme-tax-apply">Apply</Button>}
            </div>
          </div>
          {preview && (
            <div className="border border-border p-2 grid grid-cols-4 gap-2 text-xs font-mono" data-testid="msme-tax-preview-result">
              <div><span className="text-muted-foreground">Base:</span> ₹{preview.base}</div>
              <div><span className="text-muted-foreground">Rate:</span> {preview.rate}%</div>
              <div className="text-amber-600"><b>TDS:</b> ₹{preview.tds_amount}</div>
              <div className="text-emerald-600"><b>Net:</b> ₹{preview.net_payable}</div>
              <div className="col-span-4 text-[10px] italic text-muted-foreground">{preview.advisory}</div>
            </div>
          )}
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3">
        <section className="border border-border" data-testid="msme-tax-irn">
          <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold uppercase tracking-widest">E-Invoicing IRN</div>
          <div className="p-3 space-y-2">
            <Button size="sm" variant="outline" className="rounded-none" onClick={genIrn} data-testid="msme-tax-genirn" disabled={!invoice}>Generate IRN (mock)</Button>
            {irn && (
              <div className="border border-border p-2 text-[11px] font-mono">
                <div><span className="text-muted-foreground">IRN:</span> {irn.irn.slice(0, 24)}…</div>
                <div><span className="text-muted-foreground">Ack:</span> {irn.ack_no}</div>
                <div><span className="text-muted-foreground">QR:</span> {irn.signed_qr}</div>
                <div className="mt-1 chip chip-warning text-[9px]">{irn.is_mock ? "MOCK — set NIC creds for live" : "LIVE"}</div>
              </div>
            )}
          </div>
        </section>
        <section className="border border-border" data-testid="msme-tax-eway">
          <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold uppercase tracking-widest">E-Way Bill</div>
          <div className="p-3 space-y-2">
            <Button size="sm" variant="outline" className="rounded-none" onClick={genEway} data-testid="msme-tax-eway-btn" disabled={!invoice}>Generate E-way (mock)</Button>
            {eway && (
              <div className="border border-border p-2 text-[11px] font-mono">
                {eway.required === false ? <div className="italic text-muted-foreground">{eway.advisory}</div>
                  : <><div>No: {eway.eway_bill_no}</div><div>Vehicle: {eway.vehicle_no}</div></>}
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="border border-border" data-testid="msme-tax-gstr2a">
        <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold uppercase tracking-widest">
          GSTR-2A Reconciliation
        </div>
        <div className="p-3 space-y-2">
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Period (YYYY-MM)</Label>
              <Input type="month" value={gstrPeriod} onChange={(e) => setGstrPeriod(e.target.value)} className="h-9 rounded-none w-40" data-testid="msme-tax-gstr-period" />
            </div>
            <Button size="sm" variant="outline" className="rounded-none" onClick={runGstrRecon} data-testid="msme-tax-gstr-run">Reconcile</Button>
          </div>
          {gstrRecon && (
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="border border-border p-2"><div className="text-[10px] uppercase text-emerald-600">Matched</div><div className="text-lg font-mono">{gstrRecon.summary.matched}</div></div>
              <div className="border border-border p-2"><div className="text-[10px] uppercase text-amber-600">Only local</div><div className="text-lg font-mono">{gstrRecon.summary.only_in_local}</div></div>
              <div className="border border-border p-2"><div className="text-[10px] uppercase text-red-600">Only GSTN</div><div className="text-lg font-mono">{gstrRecon.summary.only_in_gstr2a}</div></div>
              <div className="border border-border p-2"><div className="text-[10px] uppercase text-orange-600">Mismatch</div><div className="text-lg font-mono">{gstrRecon.summary.amount_mismatch}</div></div>
            </div>
          )}
          <div className="text-[10px] italic text-muted-foreground">
            Upload GSTR-2A rows via <code>POST /workspaces/{'{ws}'}/gstr2a/upload</code>. Then hit Reconcile to compare with our local bills — surfaces missed ITC and unfiled supplier invoices.
          </div>
        </div>
      </section>
    </div>
  );
}


// ─────────── Phase D · CFO Copilot pane ───────────

function CfoCopilotPane({ wsId }) {
  const [ltvMode, setLtvMode] = useState("historic");
  const [ltvLifetime, setLtvLifetime] = useState(12);
  const [ltv, setLtv] = useState(null);
  const [ltvLoading, setLtvLoading] = useState(false);

  const [runwayBaseline, setRunwayBaseline] = useState(3);
  const [runway, setRunway] = useState(null);
  const [runwayLoading, setRunwayLoading] = useState(false);

  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);

  const [itc, setItc] = useState(null);
  const [itcLoading, setItcLoading] = useState(false);

  // Cohort LTV curves (Iter 161)
  const [cohortBy, setCohortBy] = useState("month");
  const [cohortHorizon, setCohortHorizon] = useState(12);
  const [cohort, setCohort] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(false);

  // Board Pack (Iter 161)
  const [boardBusy, setBoardBusy] = useState(false);

  const loadLtv = useCallback(async () => {
    if (!wsId) return;
    setLtvLoading(true);
    try {
      const params = `mode=${ltvMode}&limit=15${ltvMode === "predictive" ? `&lifetime_months=${ltvLifetime}` : ""}`;
      const r = await api.get(`/workspaces/${wsId}/cfo/customer-ltv?${params}`);
      setLtv(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "LTV load failed")); }
    finally { setLtvLoading(false); }
  }, [wsId, ltvMode, ltvLifetime]);

  const loadRunway = useCallback(async () => {
    if (!wsId) return;
    setRunwayLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/cfo/cash-runway?baseline_months=${runwayBaseline}`);
      setRunway(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Runway load failed")); }
    finally { setRunwayLoading(false); }
  }, [wsId, runwayBaseline]);

  const loadNarrative = useCallback(async (force = false) => {
    if (!wsId) return;
    setNarrativeLoading(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/cfo/budget-variance-narrative`, { force_refresh: force });
      setNarrative(r.data);
      if (force) toast.success("Narrative regenerated");
    } catch (e) { toast.error(apiErrorMessage(e, "Narrative failed")); }
    finally { setNarrativeLoading(false); }
  }, [wsId]);

  const runItcCheck = useCallback(async () => {
    if (!wsId) return;
    setItcLoading(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/cfo/missed-itc-nudge/run?dry_run=true`, {});
      setItc(r.data);
      if (r.data.skipped) toast.info("No GSTR-2A data uploaded yet");
      else toast.success(`${r.data.missed_count} missed ITC rows for ${r.data.period}`);
    } catch (e) { toast.error(apiErrorMessage(e, "ITC check failed")); }
    finally { setItcLoading(false); }
  }, [wsId]);

  useEffect(() => { loadLtv(); }, [loadLtv]);
  useEffect(() => { loadRunway(); }, [loadRunway]);
  useEffect(() => { loadNarrative(false); }, [loadNarrative]);

  // Cohort LTV loader
  const loadCohort = useCallback(async () => {
    if (!wsId) return;
    setCohortLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${wsId}/cfo/cohort-ltv?cohort_by=${cohortBy}&horizon_months=${cohortHorizon}`);
      setCohort(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Cohort load failed")); }
    finally { setCohortLoading(false); }
  }, [wsId, cohortBy, cohortHorizon]);
  useEffect(() => { loadCohort(); }, [loadCohort]);

  // Board Pack PDF download
  const downloadBoardPack = useCallback(async () => {
    if (!wsId) return;
    setBoardBusy(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/cfo/board-pack.pdf`,
                                { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `board-pack-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Board pack downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Board pack failed")); }
    finally { setBoardBusy(false); }
  }, [wsId]);

  const ccy = ltv?.currency || runway?.currency || "INR";
  const runwayColor = runway?.is_profitable
    ? "text-emerald-700"
    : (runway?.runway_months ?? 0) < 6
      ? "text-red-600"
      : (runway?.runway_months ?? 0) < 12
        ? "text-amber-600"
        : "text-emerald-700";

  return (
    <div className="space-y-6" data-testid="cfo-copilot-pane">
      {/* ─────────── Cash Runway ─────────── */}
      <section className="rounded-none border bg-white">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-emerald-700" />
            <h3 className="text-sm font-semibold">Cash Runway Forecast</h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(runwayBaseline)} onValueChange={(v) => setRunwayBaseline(Number(v))}>
              <SelectTrigger className="h-8 w-[180px] rounded-none text-xs" data-testid="cfo-runway-baseline">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Trailing 3 months</SelectItem>
                <SelectItem value="6">Trailing 6 months</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="rounded-none" onClick={loadRunway} disabled={runwayLoading} data-testid="cfo-runway-refresh">
              <RotateCw className={`h-3.5 w-3.5 ${runwayLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </header>
        <div className="grid grid-cols-2 gap-0 md:grid-cols-5">
          <StatBox label="Cash on hand" value={fmt(runway?.cash_on_hand, ccy)} testid="cfo-runway-cash" />
          <StatBox label="Monthly revenue" value={fmt(runway?.monthly_revenue, ccy)} testid="cfo-runway-rev" />
          <StatBox label="Monthly expense" value={fmt(runway?.monthly_expense, ccy)} testid="cfo-runway-exp" />
          <StatBox label="Net burn / mo" value={fmt(runway?.net_burn, ccy)} testid="cfo-runway-burn" />
          <div className="border-t p-4 md:border-l md:border-t-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">Runway</div>
            <div className={`mt-1 text-2xl font-semibold ${runwayColor}`} data-testid="cfo-runway-months">
              {runway?.is_profitable
                ? "Profitable"
                : runway?.runway_months == null
                  ? "—"
                  : `${runway.runway_months} mo`}
            </div>
            {runway?.runway_with_ar_months != null && !runway?.is_profitable && (
              <div className="text-xs text-gray-500">+ AR: {runway.runway_with_ar_months} mo</div>
            )}
          </div>
        </div>
        {runway?.trailing?.length > 0 && (
          <div className="border-t p-4">
            <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Trailing months</div>
            <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
              {runway.trailing.map((row) => (
                <div key={row.month} className="rounded-none border p-2" data-testid={`cfo-runway-mo-${row.month}`}>
                  <div className="text-[10px] text-gray-500">{row.month}</div>
                  <div className="text-xs">In {fmt(row.revenue, ccy)}</div>
                  <div className="text-xs">Out {fmt(row.expense, ccy)}</div>
                  <div className={`text-xs font-semibold ${row.net >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    Net {fmt(row.net, ccy)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ─────────── Customer LTV ─────────── */}
      <section className="rounded-none border bg-white">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-700" />
            <h3 className="text-sm font-semibold">Customer LTV</h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={ltvMode} onValueChange={setLtvMode}>
              <SelectTrigger className="h-8 w-[200px] rounded-none text-xs" data-testid="cfo-ltv-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="historic">Historic (retrospective)</SelectItem>
                <SelectItem value="predictive">Predictive (next N months)</SelectItem>
              </SelectContent>
            </Select>
            {ltvMode === "predictive" && (
              <Select value={String(ltvLifetime)} onValueChange={(v) => setLtvLifetime(Number(v))}>
                <SelectTrigger className="h-8 w-[130px] rounded-none text-xs" data-testid="cfo-ltv-horizon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="24">24 months</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button size="sm" variant="outline" className="rounded-none" onClick={loadLtv} disabled={ltvLoading} data-testid="cfo-ltv-refresh">
              <RotateCw className={`h-3.5 w-3.5 ${ltvLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </header>
        <div className="grid grid-cols-3 gap-0 border-b">
          <StatBox label="Customers" value={ltv?.customer_count ?? "—"} testid="cfo-ltv-count" />
          <StatBox label="Total historic revenue" value={fmt(ltv?.summary?.total_historic_revenue, ccy)} testid="cfo-ltv-total" />
          <StatBox label="Avg LTV" value={fmt(ltv?.summary?.average_ltv, ccy)} testid="cfo-ltv-avg" />
        </div>
        {ltv?.summary?.high_risk_customers > 0 && (
          <div className="border-b bg-red-50 px-4 py-2 text-xs text-red-800">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            <b>{ltv.summary.high_risk_customers}</b> customer(s) haven&apos;t invoiced in 6+ months — churn risk.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="cfo-ltv-table">
            <thead className="bg-gray-50 text-left uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2 text-right">Invoices</th>
                <th className="px-3 py-2 text-right">Historic</th>
                <th className="px-3 py-2 text-right">Avg / invoice</th>
                <th className="px-3 py-2 text-right">Freq / mo</th>
                {ltvMode === "predictive" && <th className="px-3 py-2 text-right">Predicted</th>}
                <th className="px-3 py-2 text-right">Total LTV</th>
                <th className="px-3 py-2">Churn</th>
              </tr>
            </thead>
            <tbody>
              {(ltv?.top_customers || []).map((row) => (
                <tr key={row.client_id} className="border-t" data-testid={`cfo-ltv-row-${row.client_id}`}>
                  <td className="px-3 py-2 font-medium">{row.client_name}</td>
                  <td className="px-3 py-2 text-right">{row.invoices}</td>
                  <td className="px-3 py-2 text-right">{fmt(row.historic_revenue, ccy)}</td>
                  <td className="px-3 py-2 text-right">{fmt(row.avg_invoice_value, ccy)}</td>
                  <td className="px-3 py-2 text-right">{row.monthly_frequency}</td>
                  {ltvMode === "predictive" && (
                    <td className="px-3 py-2 text-right text-blue-700">{fmt(row.predicted_next_ltv, ccy)}</td>
                  )}
                  <td className="px-3 py-2 text-right font-semibold">{fmt(row.total_ltv, ccy)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded-none px-1.5 py-0.5 text-[10px] font-medium ${
                      row.churn_risk === "high" ? "bg-red-100 text-red-700"
                      : row.churn_risk === "medium" ? "bg-amber-100 text-amber-800"
                      : row.churn_risk === "low" ? "bg-emerald-100 text-emerald-800"
                      : "bg-gray-100 text-gray-600"}`}>
                      {row.churn_risk}
                    </span>
                  </td>
                </tr>
              ))}
              {!ltvLoading && (!ltv?.top_customers || ltv.top_customers.length === 0) && (
                <tr><td colSpan={ltvMode === "predictive" ? 8 : 7} className="px-3 py-6 text-center text-gray-500">No customer invoices yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ─────────── Cohort LTV curves (Iter 161) ─────────── */}
      <section className="rounded-none border bg-white" data-testid="cfo-cohort-section">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-indigo-700" />
            <h3 className="text-sm font-semibold">Cohort LTV curves</h3>
            <span className="text-[10px] uppercase tracking-widest text-gray-400">
              Clients grouped by first-invoice period
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={cohortBy} onValueChange={setCohortBy}>
              <SelectTrigger className="h-8 w-[140px] rounded-none text-xs" data-testid="cfo-cohort-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">By month</SelectItem>
                <SelectItem value="quarter">By quarter</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(cohortHorizon)} onValueChange={(v) => setCohortHorizon(Number(v))}>
              <SelectTrigger className="h-8 w-[130px] rounded-none text-xs" data-testid="cfo-cohort-horizon">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6 mo horizon</SelectItem>
                <SelectItem value="12">12 mo horizon</SelectItem>
                <SelectItem value="18">18 mo horizon</SelectItem>
                <SelectItem value="24">24 mo horizon</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="rounded-none"
                     onClick={loadCohort} disabled={cohortLoading}
                     data-testid="cfo-cohort-refresh">
              <RotateCw className={`h-3.5 w-3.5 ${cohortLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </header>
        {cohort && (
          <>
            <div className="grid grid-cols-2 gap-0 md:grid-cols-4 border-b">
              <StatBox label="Cohorts" value={cohort.summary?.cohort_count ?? 0} testid="cfo-cohort-count" />
              <StatBox label="Total clients" value={cohort.summary?.total_clients ?? 0} testid="cfo-cohort-total-clients" />
              <StatBox
                label={`Best · ${cohort.summary?.best_cohort || "—"}`}
                value={fmt(cohort.summary?.best_avg_ltv, ccy)}
                testid="cfo-cohort-best"
              />
              <StatBox
                label={`Weakest · ${cohort.summary?.weakest_cohort || "—"}`}
                value={fmt(cohort.summary?.weakest_avg_ltv, ccy)}
                testid="cfo-cohort-weakest"
              />
            </div>
            {(cohort.cohorts || []).length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-500">No cohort data yet — needs at least one client with a posted invoice.</div>
            ) : (
              <div className="p-4">
                <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">
                  Cumulative avg revenue per client (across months since first invoice)
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs" data-testid="cfo-cohort-table">
                    <thead className="bg-gray-50 text-left uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Cohort</th>
                        <th className="px-3 py-2 text-right">Clients</th>
                        <th className="px-3 py-2 text-right">M0</th>
                        <th className="px-3 py-2 text-right">M1</th>
                        <th className="px-3 py-2 text-right">M3</th>
                        <th className="px-3 py-2 text-right">M6</th>
                        <th className="px-3 py-2 text-right">M{Math.min(11, cohortHorizon - 1)}</th>
                        <th className="px-3 py-2 text-right">Avg LTV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cohort.cohorts.map((c) => {
                        const at = (i) => c.months.find((m) => m.month_index === i)?.cumulative_avg_per_client;
                        return (
                          <tr key={c.cohort} className="border-t"
                              data-testid={`cfo-cohort-row-${c.cohort}`}>
                            <td className="px-3 py-2 font-mono">{c.cohort}</td>
                            <td className="px-3 py-2 text-right">{c.client_count}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmt(at(0), ccy)}</td>
                            <td className="px-3 py-2 text-right font-mono text-gray-500">{fmt(at(1), ccy)}</td>
                            <td className="px-3 py-2 text-right font-mono text-gray-500">{fmt(at(3), ccy)}</td>
                            <td className="px-3 py-2 text-right font-mono text-gray-500">{fmt(at(6), ccy)}</td>
                            <td className="px-3 py-2 text-right font-mono text-gray-500">{fmt(at(Math.min(11, cohortHorizon - 1)), ccy)}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(c.avg_ltv_per_client, ccy)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* ─────────── Quarterly Board Pack PDF (Iter 161) ─────────── */}
      <section className="rounded-none border bg-white" data-testid="cfo-boardpack-section">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-semibold">Quarterly Board Pack</h3>
            <span className="text-[10px] uppercase tracking-widest text-gray-400">
              One-click PDF for board / investor review
            </span>
          </div>
          <Button size="sm" onClick={downloadBoardPack} disabled={boardBusy}
                   className="rounded-none" data-testid="cfo-download-board-pack">
            {boardBusy ? "Building…" : "Download PDF"}
          </Button>
        </header>
        <div className="p-4 text-xs text-gray-600">
          Auto-assembles: current-quarter KPIs (invoiced, collected, deals won),
          AR outstanding + at-risk, DSO snapshot, and top 5 clients. Runs off
          your live ledger — no separate export needed.
        </div>
      </section>

      {/* ─────────── AI Budget Variance Narrative ─────────── */}
      <section className="rounded-none border bg-white">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-700" />
            <h3 className="text-sm font-semibold">AI Budget-Variance Narrative</h3>
            {narrative?.from_cache && (
              <span className="rounded-none bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600" data-testid="cfo-narrative-cache-badge">cached · {narrative.month_key}</span>
            )}
          </div>
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => loadNarrative(true)} disabled={narrativeLoading} data-testid="cfo-narrative-refresh">
            <RotateCw className={`mr-1 h-3.5 w-3.5 ${narrativeLoading ? "animate-spin" : ""}`} />
            Regenerate
          </Button>
        </header>
        {narrative ? (
          <div className="space-y-3 p-4">
            <div className="text-base font-semibold" data-testid="cfo-narrative-headline">{narrative.headline}</div>
            <p className="text-sm leading-relaxed text-gray-800" data-testid="cfo-narrative-body">{narrative.narrative}</p>
            {narrative.highlights?.length > 0 && (
              <div>
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">Highlights</div>
                <ul className="space-y-1 text-xs">
                  {narrative.highlights.map((h, i) => (
                    <li key={i} className="rounded-none border-l-2 border-purple-300 bg-purple-50 px-2 py-1">
                      <span className="font-semibold">{h.period} · {h.kind} {h.direction}</span>
                      {typeof h.drift_pct === "number" && <span className="ml-1 text-gray-600">({h.drift_pct}%)</span>}
                      {h.driver && <div className="text-gray-700">{h.driver}</div>}
                      {h.note && <div className="text-gray-600">{h.note}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {narrative.recommendations?.length > 0 && (
              <div>
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">Recommendations</div>
                <ol className="ml-4 list-decimal space-y-1 text-xs text-gray-800">
                  {narrative.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                </ol>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-xs text-gray-500">
            {narrativeLoading ? "Generating narrative…" : "Click Regenerate to build a fresh narrative"}
          </div>
        )}
      </section>

      {/* ─────────── Missed ITC weekly nudge ─────────── */}
      <section className="rounded-none border bg-white">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-amber-700" />
            <h3 className="text-sm font-semibold">Missed ITC Watch</h3>
            <span className="text-[10px] text-gray-500">Auto-runs Monday 09:15 UTC</span>
          </div>
          <Button size="sm" className="rounded-none" onClick={runItcCheck} disabled={itcLoading} data-testid="cfo-itc-run">
            <RotateCw className={`mr-1 h-3.5 w-3.5 ${itcLoading ? "animate-spin" : ""}`} />
            Check now
          </Button>
        </header>
        <div className="p-4">
          {itc ? (
            itc.skipped ? (
              <div className="text-xs text-gray-500">No GSTR-2A rows uploaded yet. Upload GSTR-2A JSON in Tax &amp; compliance → GSTR-2A tab first.</div>
            ) : (
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Latest period:</span>{" "}
                  <span className="font-semibold" data-testid="cfo-itc-period">{itc.period}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <div className="text-xs text-gray-500">Missed rows</div>
                    <div className={`text-2xl font-semibold ${itc.missed_count > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="cfo-itc-count">
                      {itc.missed_count}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Unclaimed tax</div>
                    <div className="text-2xl font-semibold" data-testid="cfo-itc-tax">
                      {fmt(itc.unclaimed_tax, ccy)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Recipients</div>
                    <div className="text-2xl font-semibold">{itc.recipients ?? 0}</div>
                  </div>
                </div>
                {itc.missed_count > 0 && (
                  <div className="mt-2 rounded-none border-l-4 border-amber-500 bg-amber-50 p-2 text-xs">
                    <b>Action needed:</b> Book these bills in Tax &amp; Compliance → GSTR-2A recon before the ITC claim window closes.
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="text-xs text-gray-500">Click &quot;Check now&quot; to preview the weekly missed-ITC alert.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="border-t p-4 md:border-l md:border-t-0" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

// ─────────── Phase E · Doc Templates pane ───────────

const TEMPLATE_KINDS = [
  { key: "invoice", label: "Invoice" },
  { key: "quotation", label: "Quotation" },
  { key: "purchase_order", label: "Purchase Order" },
];

function DocTemplatesPane({ wsId }) {
  const [templates, setTemplates] = useState(null);
  const [kind, setKind] = useState("invoice");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const r = await api.get(`/workspaces/${wsId}/doc-templates`);
      setTemplates(r.data);
      setDraft(r.data[kind] || {});
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [wsId, kind]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (templates) setDraft(templates[kind] || {});
  }, [kind, templates]);

  const patch = (fields) => setDraft((d) => ({ ...d, ...fields }));

  const save = async () => {
    if (!wsId) return;
    setSaving(true);
    try {
      const r = await api.patch(`/workspaces/${wsId}/doc-templates/${kind}`, {
        accent_color: draft.accent_color,
        header_html: draft.header_html,
        footer_html: draft.footer_html,
        terms_html: draft.terms_html,
        show_logo: draft.show_logo,
        show_gstin: draft.show_gstin,
        show_qr: draft.show_qr,
        show_signature: draft.show_signature,
        currency_position: draft.currency_position,
        font_family: draft.font_family,
      });
      setTemplates((t) => ({ ...(t || {}), [kind]: r.data }));
      toast.success("Template saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!wsId) return;
    if (!window.confirm("Reset this template to platform defaults?")) return;
    try {
      const r = await api.post(`/workspaces/${wsId}/doc-templates/${kind}/reset`, {});
      setTemplates((t) => ({ ...(t || {}), [kind]: r.data }));
      setDraft(r.data);
      toast.success("Reset to defaults");
    } catch (e) { toast.error(apiErrorMessage(e, "Reset failed")); }
  };

  if (!templates) {
    return <div className="p-8 text-center text-xs text-gray-500">Loading templates…</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="doc-templates-pane">
      <section className="rounded-none border bg-white">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-purple-700" />
            <h3 className="text-sm font-semibold">Document templates</h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-8 w-[180px] rounded-none text-xs" data-testid="tpl-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_KINDS.map((k) => (
                  <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="rounded-none" onClick={reset} data-testid="tpl-reset">Reset</Button>
            <Button size="sm" className="rounded-none" onClick={save} disabled={saving} data-testid="tpl-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </header>
        <div className="space-y-4 p-4">
          <div>
            <Label className="text-xs">Accent color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={draft.accent_color || "#0ea5e9"}
                onChange={(e) => patch({ accent_color: e.target.value })}
                className="h-9 w-16 cursor-pointer rounded-none border p-0"
                data-testid="tpl-accent-color"
              />
              <Input
                value={draft.accent_color || ""}
                onChange={(e) => patch({ accent_color: e.target.value })}
                className="rounded-none font-mono"
                placeholder="#0ea5e9"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Font family</Label>
            <Select value={draft.font_family || "Helvetica"} onValueChange={(v) => patch({ font_family: v })}>
              <SelectTrigger className="rounded-none" data-testid="tpl-font">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Helvetica">Helvetica (Sans)</SelectItem>
                <SelectItem value="Times-Roman">Times Roman (Serif)</SelectItem>
                <SelectItem value="Courier">Courier (Mono)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Footer text</Label>
            <Input
              value={draft.footer_html || ""}
              onChange={(e) => patch({ footer_html: e.target.value })}
              maxLength={400}
              placeholder="Thank you for your business."
              className="rounded-none"
              data-testid="tpl-footer"
            />
          </div>
          <div>
            <Label className="text-xs">Terms &amp; conditions</Label>
            <Textarea
              value={draft.terms_html || ""}
              onChange={(e) => patch({ terms_html: e.target.value })}
              rows={5}
              maxLength={2000}
              className="rounded-none"
              data-testid="tpl-terms"
            />
            <div className="mt-1 text-[10px] text-gray-500">{(draft.terms_html || "").length}/2000 · Plain text only, no HTML</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: "show_logo", label: "Show logo" },
              { key: "show_gstin", label: "Show GSTIN" },
              { key: "show_qr", label: "Show UPI QR" },
              { key: "show_signature", label: "Show signature block" },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 rounded-none border px-3 py-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!draft[key]}
                  onChange={(e) => patch({ [key]: e.target.checked })}
                  data-testid={`tpl-toggle-${key}`}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-none border bg-white">
        <header className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Live preview</h3>
        </header>
        <div className="p-4">
          <div
            className="rounded-none border shadow-sm"
            style={{ fontFamily: draft.font_family === "Times-Roman" ? "serif" : draft.font_family === "Courier" ? "monospace" : "system-ui" }}
            data-testid="tpl-preview"
          >
            <div className="h-1.5" style={{ background: draft.accent_color || "#0ea5e9" }} />
            <div className="flex items-start justify-between gap-4 border-b p-4">
              <div>
                {draft.show_logo && (
                  <div className="mb-2 h-10 w-24 rounded-none bg-gray-200 text-center text-[10px] leading-10 text-gray-500">LOGO</div>
                )}
                <div className="text-lg font-bold">Demo Workspace</div>
                <div className="text-xs text-gray-600">Sample address</div>
                {draft.show_gstin && (
                  <div className="text-xs text-gray-600">GSTIN: 27AAACS0000A1Z0</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold uppercase">{kind === "invoice" ? "Invoice" : kind === "quotation" ? "Quote" : "PO"}</div>
                <div className="text-xs text-gray-500"># DEMO-001</div>
                <div className="mt-2 inline-block rounded-none px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: draft.accent_color || "#0ea5e9" }}>SENT</div>
              </div>
            </div>
            <div className="border-b p-4">
              <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">Bill to</div>
              <div className="text-sm font-semibold">ACME Corp</div>
              <div className="text-xs text-gray-600">buyer@example.com</div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-left uppercase text-gray-500">
                <tr>
                  <th className="p-2">#</th>
                  <th className="p-2">Description</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2 text-right">Rate</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t"><td className="p-2">1</td><td className="p-2">Sample line item</td><td className="p-2 text-right">2</td><td className="p-2 text-right">₹5,000</td><td className="p-2 text-right">₹10,000</td></tr>
                <tr className="border-t"><td className="p-2">2</td><td className="p-2">Another item</td><td className="p-2 text-right">1</td><td className="p-2 text-right">₹3,500</td><td className="p-2 text-right">₹3,500</td></tr>
              </tbody>
            </table>
            <div className="border-t p-4 text-right text-sm">
              <div className="text-gray-600">Subtotal: ₹13,500</div>
              <div className="text-gray-600">Tax: ₹2,430</div>
              <div className="text-lg font-bold">Grand Total: ₹15,930</div>
            </div>
            {draft.terms_html && (
              <div className="border-t p-4">
                <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">Terms</div>
                <div className="whitespace-pre-line text-xs text-gray-700">{draft.terms_html}</div>
              </div>
            )}
            {draft.show_signature && (
              <div className="border-t p-4 text-right text-xs text-gray-500">
                <div className="ml-auto w-40 border-t pt-1">Authorised Signatory</div>
              </div>
            )}
            <div className="border-t bg-gray-50 p-3 text-center text-[10px] text-gray-500">
              {draft.footer_html || "Powered by Acquilix"}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────── Phase E · Notification Preferences pane ───────────

export function NotificationPrefsPane() {
  const [categories, setCategories] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get("/notifications/categories"),
        api.get("/notifications/preferences"),
      ]);
      setCategories(c.data);
      setPrefs(p.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key) => {
    if (busy) return;
    const next = { ...prefs, [key]: !prefs[key] };
    const prev = prefs;
    setPrefs(next);
    setBusy(true);
    try {
      await api.put("/notifications/preferences", { [key]: next[key] });
      toast.success(next[key] ? "Category enabled" : "Category muted");
    } catch (e) {
      setPrefs(prev);
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="notif-prefs-pane">
      <section className="rounded-none border bg-white">
        <header className="flex items-center gap-2 border-b px-4 py-3">
          <BellRing className="h-4 w-4 text-blue-700" />
          <h3 className="text-sm font-semibold">Notification preferences</h3>
        </header>
        <div className="p-4">
          <p className="mb-4 text-xs text-gray-600">
            Toggle categories on or off. Muted categories will still be visible in the archive but won&apos;t generate new in-app pushes or trigger email alerts.
          </p>
          <div className="space-y-2">
            {categories.map((cat) => (
              <div key={cat.key} className="flex items-center justify-between rounded-none border px-4 py-3">
                <div>
                  <div className="text-sm font-semibold">{cat.label}</div>
                  <div className="text-xs text-gray-600">{cat.description}</div>
                </div>
                <label className="inline-flex cursor-pointer items-center" data-testid={`notif-toggle-${cat.key}`}>
                  <input
                    type="checkbox"
                    checked={!!prefs[cat.key]}
                    onChange={() => toggle(cat.key)}
                    className="peer sr-only"
                  />
                  <span className="relative inline-block h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-emerald-500">
                    <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition peer-checked:translate-x-5" />
                  </span>
                </label>
              </div>
            ))}
          </div>
          {/* Feb 2026 · Per-channel mute for Voice Campaign alerts. The
              server-side `user_wants_channel` helper reads these keys
              before firing each channel. */}
          <div className="mt-6 border-t pt-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
              Voice campaigns
            </div>
            <p className="mb-3 text-xs text-gray-600">
              Fine-grained control over the alerts fired when a voice campaign finishes, plus the weekly leaderboard email.
            </p>
            <div className="space-y-2">
              {[
                { key: "voice_campaigns_in_app",  label: "In-app toast + bell",       desc: "Instant push when a campaign closes." },
                { key: "voice_campaigns_email",   label: "Email summary to creator",  desc: "One-shot email with the final campaign stats." },
                { key: "voice_campaigns_whatsapp",label: "WhatsApp ping (Interakt)",  desc: "Best-effort session message — needs Interakt configured." },
                { key: "voice_campaign_digest",   label: "Nightly agent leaderboard", desc: "7-day connect-rate / booked-rate / avg call length." },
                { key: "voice_score_alerts",      label: "Score-drop alerts",         desc: "Daily ping when any agent's 7-day score drops more than 10 pts." },
              ].map((row) => (
                <div key={row.key} className="flex items-center justify-between rounded-none border px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold">{row.label}</div>
                    <div className="text-xs text-gray-600">{row.desc}</div>
                  </div>
                  <label className="inline-flex cursor-pointer items-center" data-testid={`notif-toggle-${row.key}`}>
                    <input
                      type="checkbox"
                      checked={prefs[row.key] !== false}
                      onChange={() => toggle(row.key)}
                      className="peer sr-only"
                    />
                    <span className="relative inline-block h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-emerald-500">
                      <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition peer-checked:translate-x-5" />
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────── Phase E · Ledger Export pane (Tally + Zoho) ───────────

function LedgerExportPane({ wsId }) {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [downloading, setDownloading] = useState(null);

  const download = async (kind, url, filename) => {
    if (!wsId) return;
    setDownloading(kind);
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`${kind} downloaded`);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
    finally { setDownloading(null); }
  };

  const tally = () => download(
    "Tally XML",
    `/workspaces/${wsId}/ledger-exports/tally-xml?from_date=${fromDate}&to_date=${toDate}`,
    `tally-${fromDate}-to-${toDate}.xml`,
  );
  const zoho = (kind) => download(
    `Zoho ${kind}`,
    `/workspaces/${wsId}/ledger-exports/zoho-csv?from_date=${fromDate}&to_date=${toDate}&kind=${kind}`,
    `zoho-${kind}-${fromDate}-to-${toDate}.csv`,
  );

  return (
    <div className="max-w-4xl space-y-6" data-testid="ledger-export-pane">
      <section className="rounded-none border bg-white">
        <header className="flex items-center gap-2 border-b px-4 py-3">
          <Download className="h-4 w-4 text-emerald-700" />
          <h3 className="text-sm font-semibold">Ledger exports</h3>
        </header>
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <div>
            <Label className="text-xs">From date</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-none" data-testid="export-from" />
          </div>
          <div>
            <Label className="text-xs">To date</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-none" data-testid="export-to" />
          </div>
        </div>
      </section>

      <section className="rounded-none border bg-white">
        <header className="border-b bg-amber-50 px-4 py-3">
          <div className="text-sm font-semibold">Tally Prime — XML voucher import</div>
          <div className="text-xs text-gray-600">Streams posted journal entries as Tally-XML. Import from Gateway → Import Data → Vouchers.</div>
        </header>
        <div className="p-4">
          <Button className="rounded-none" onClick={tally} disabled={downloading === "Tally XML"} data-testid="export-tally-btn">
            <Download className="mr-1 h-3.5 w-3.5" />
            {downloading === "Tally XML" ? "Preparing…" : "Download Tally XML"}
          </Button>
        </div>
      </section>

      <section className="rounded-none border bg-white">
        <header className="border-b bg-blue-50 px-4 py-3">
          <div className="text-sm font-semibold">Zoho Books — CSV imports</div>
          <div className="text-xs text-gray-600">Choose the entity you want to migrate. Each file matches Zoho&apos;s official CSV import template.</div>
        </header>
        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          {[
            { kind: "journal", label: "Journal entries" },
            { kind: "invoices", label: "Invoices" },
            { kind: "bills", label: "Bills" },
            { kind: "payments", label: "Payments" },
          ].map((z) => (
            <Button
              key={z.kind}
              variant="outline"
              className="rounded-none"
              onClick={() => zoho(z.kind)}
              disabled={downloading === `Zoho ${z.kind}`}
              data-testid={`export-zoho-${z.kind}`}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              {z.label}
            </Button>
          ))}
        </div>
      </section>
    </div>
  );
}

